function toDateKey(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

function addDaysToDateKey(dateKeyOrIso, dayDelta) {
  const key = toDateKey(dateKeyOrIso);
  if (!key || !Number.isFinite(dayDelta)) return null;
  const [y, m, d] = key.split('-').map((n) => Number(n));
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + dayDelta);
  return toDateKey(dt);
}

function readPackageStartDay(pkg) {
  if (!pkg || typeof pkg !== 'object') return 0;
  const raw = pkg.startDay ?? pkg.start_day ?? pkg.startDayOffset;
  if (raw == null || raw === '') return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function resolveMainPackage(packages, selectedPkg) {
  if (selectedPkg?.isMain) return selectedPkg;
  const list = Array.isArray(packages) ? packages : [];
  return list.find((p) => p.isMain) || selectedPkg || list[0] || null;
}

/** Package calendar dates for receipts (join city departure, not admin event batch). */
export function computePackageDates(event, pkg, trekPackages = []) {
  if (!event?.startDate) {
    return { packageStartDate: null, packageEndDate: null };
  }
  if (!pkg) {
    return {
      packageStartDate: toDateKey(event.startDate),
      packageEndDate: toDateKey(event.endDate)
    };
  }
  const mainPkg = resolveMainPackage(trekPackages, pkg);
  const refStartDay = readPackageStartDay(mainPkg || pkg);
  const activeStartDay = readPackageStartDay(pkg);
  const startShift = activeStartDay - refStartDay;
  const packageStartDate = addDaysToDateKey(event.startDate, startShift);

  const normalizeIds = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      try {
        const p = JSON.parse(val);
        return Array.isArray(p) ? p : [];
      } catch {
        return val.split(',').map((s) => s.trim()).filter(Boolean);
      }
    }
    return [];
  };

  const enabledIds = normalizeIds(pkg.enabledScheduleIds ?? pkg.enabled_schedule_ids);
  let durationDays = null;
  if (enabledIds.length > 0) {
    durationDays = enabledIds.length;
  } else if (event.startDate && event.endDate) {
    const s = new Date(event.startDate);
    const e = new Date(event.endDate);
    const diff = Math.round((e - s) / (24 * 60 * 60 * 1000)) + 1;
    if (diff > 0) {
      const rawEnd = pkg.endDay ?? pkg.end_day ?? pkg.endDayOffset;
      const refEnd = mainPkg?.endDay ?? mainPkg?.end_day ?? mainPkg?.endDayOffset;
      const endShift = (Number(rawEnd) || 0) - (Number(refEnd) || 0);
      durationDays = Math.max(1, diff - startShift - endShift);
    }
  }

  if (!durationDays || durationDays <= 0) {
    const startDay = Number(pkg.startDay ?? pkg.start_day ?? 0);
    const endDay = Number(pkg.endDay ?? pkg.end_day ?? startDay);
    durationDays = Math.max(1, endDay - startDay + 1);
  }

  const packageEndDate = packageStartDate
    ? addDaysToDateKey(packageStartDate, durationDays - 1)
    : toDateKey(event.endDate);
  return { packageStartDate, packageEndDate };
}

export function formatPackageDateRange(startDate, endDate, formatDate) {
  if (!startDate && !endDate) return '—';
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  if (!endDate || start === end) return start;
  return `${start} – ${end}`;
}
